import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import camelcaseKeys from 'camelcase-keys'
import snakecaseKeys from 'snakecase-keys'
import type { ApiResponse } from '@/types/api'

export interface ApiError extends Error {
  code?: string
  status?: number
  details?: Array<{
    field: string
    message: string
  }>
}

// Token refresh queue — prevent multiple concurrent refresh calls
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token as string)
    }
  })
  failedQueue = []
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Uses fetch (not apiClient) to avoid triggering the interceptor again.
 * Returns the new access token on success, throws on failure.
 */
const attemptTokenRefresh = async (): Promise<string> => {
  const storedTokens = localStorage.getItem('tokens')
  if (!storedTokens) {
    throw new Error('No refresh token available')
  }

  let refreshToken: string
  try {
    const parsed = JSON.parse(storedTokens) as { refreshToken?: string }
    refreshToken = parsed.refreshToken || ''
  } catch {
    throw new Error('Failed to parse stored tokens')
  }

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1'
  const response = await fetch(`${apiUrl}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    throw new Error('Token refresh failed')
  }

  const data = await response.json()

  // Backend may return tokens under data.data.tokens or data.tokens
  const tokens = (data?.data?.tokens ?? data?.tokens) as { accessToken?: string; refreshToken?: string } | undefined
  const newAccessToken = tokens?.accessToken || (data?.data?.access_token ?? data?.access_token) as string | undefined

  if (!newAccessToken) {
    throw new Error('No access token in refresh response')
  }

  // Persist updated tokens
  const updatedTokens = {
    accessToken: newAccessToken,
    refreshToken: tokens?.refreshToken || refreshToken,
  }
  localStorage.setItem('authToken', newAccessToken)
  localStorage.setItem('tokens', JSON.stringify(updatedTokens))

  return newAccessToken
}

// Axios instance configuration
export const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  return client
}

let apiClientOriginal: AxiosInstance | null = null

const getApiClient = (): AxiosInstance => {
  if (!apiClientOriginal) {
    apiClientOriginal = createApiClient()
    setupInterceptors()
  }
  return apiClientOriginal
}

// For testing purposes - allows resetting the client and refresh state
export const resetApiClient = (): void => {
  apiClientOriginal = null
  isRefreshing = false
  failedQueue = []
}

// Setup interceptors
export const setupInterceptors = (client?: AxiosInstance): void => {
  const targetClient = client || apiClientOriginal
  if (!targetClient) return

  // Request interceptor - add auth token and transform data to snake_case
  targetClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('authToken')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // Transform request data from camelCase to snake_case for backend
      // Skip FormData — snakecaseKeys only works on plain objects
      if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
        config.data = snakecaseKeys(config.data, { deep: true })
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor
  targetClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Transform snake_case to camelCase for all response data
      if (response.data) {
        response.data = camelcaseKeys(response.data, { deep: true })
      }

      // Return data directly from response
      return response.data
    },
    (error: AxiosError) => {
      const apiError: ApiError = new Error('An error occurred')

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response

        // Handle unauthorized access
        // Don't dispatch logout for auth endpoints (login/register/logout/refresh) - let them handle errors themselves
        if (status === 401) {
          const requestUrl = error.config?.url || ''
          const isAuthEndpoint = requestUrl.includes('/auth/')

          if (!isAuthEndpoint) {
            const originalConfig = error.config

            // If a refresh is already in progress, queue this request
            if (isRefreshing) {
              return new Promise((resolve, reject) => {
                failedQueue.push({
                  resolve: (token: string) => {
                    if (originalConfig) {
                      originalConfig.headers = originalConfig.headers || {}
                      originalConfig.headers['Authorization'] = `Bearer ${token}`
                    }
                    resolve(targetClient!.request(originalConfig!))
                  },
                  reject,
                })
              })
            }

            isRefreshing = true

            return new Promise((resolve, reject) => {
              attemptTokenRefresh()
                .then((newToken) => {
                  processQueue(null, newToken)
                  if (originalConfig) {
                    originalConfig.headers = originalConfig.headers || {}
                    originalConfig.headers['Authorization'] = `Bearer ${newToken}`
                    resolve(targetClient!.request(originalConfig))
                  } else {
                    resolve(undefined as unknown as AxiosResponse)
                  }
                })
                .catch((refreshError) => {
                  processQueue(refreshError, null)
                  localStorage.removeItem('authToken')
                  localStorage.removeItem('tokens')
                  localStorage.removeItem('user')
                  // Dispatch logout event - let React Router handle navigation
                  window.dispatchEvent(new Event('auth-logout'))
                  reject(refreshError)
                })
                .finally(() => {
                  isRefreshing = false
                })
            })
          }
        }

        // 403 handling: surface auth-required flows (email verification / permission denied)
        // via global events so routers can redirect.
        if (status === 403 && data && typeof data === 'object') {
          const errorData = data as { error?: { code?: string; redirect_to?: string } }
          const code = errorData.error?.code
          if (code === 'EMAIL_NOT_VERIFIED') {
            window.dispatchEvent(new CustomEvent('auth-email-not-verified', { detail: errorData.error }))
          } else if (code === 'PERMISSION_DENIED') {
            window.dispatchEvent(new CustomEvent('auth-permission-denied', { detail: errorData.error }))
          }
        }

        // Extract error details from response
        if (data && typeof data === 'object') {
          const errorData = data as any
          if (errorData.error) {
            apiError.message = errorData.error.message || 'An error occurred'
            apiError.code = errorData.error.code
            apiError.status = status
            apiError.details = errorData.error.details
          } else if (errorData.message) {
            apiError.message = errorData.message
            apiError.status = status
          }
        } else {
          apiError.message = error.message || `Error ${status}`
          apiError.status = status
        }
      } else if (error.request) {
        // The request was made but no response was received
        apiError.message = 'Network error. Please check your connection.'
      } else {
        // Something happened in setting up the request
        apiError.message = error.message || 'An error occurred'
      }

      return Promise.reject(apiError)
    }
  )
}

// Type-safe API methods
export const apiClient = {
  get: async <T>(url: string, config = {}): Promise<ApiResponse<T>> => {
    return getApiClient().get(url, config)
  },

  post: async <T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> => {
    return getApiClient().post(url, data, config)
  },

  put: async <T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> => {
    return getApiClient().put(url, data, config)
  },

  patch: async <T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> => {
    return getApiClient().patch(url, data, config)
  },

  delete: async <T>(url: string, config = {}): Promise<ApiResponse<T>> => {
    return getApiClient().delete(url, config)
  },
}

export default apiClient

// Export apiClient as api for convenience
export { apiClient as api }